import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { MapPin, Navigation, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import IndiaAddressAutocomplete from "../IndiaAddressAutocomplete";

/**
 * SmartAddressInput — Phase 3, P1
 * ──────────────────────────────
 * A single "smart" address field that progressively reveals the
 * structured fields (house no, street, flat / apt) only after the
 * customer has picked a base address. Replaces the 4-field
 * always-visible block that previously cluttered the booking form.
 *
 * Behaviours:
 *   1. Always-visible row : autocomplete + "Use current location" +
 *      map-picker button.
 *   2. Once `value` is non-empty (typed enough or picked from
 *      autocomplete) the "Add flat / house no." disclosure appears.
 *   3. Inside the disclosure: 3 free-form structured inputs that
 *      write directly back to the parent form via `onFieldChange`.
 *
 * Backward compatible — the parent form keeps the SAME shape
 * (pickup_address, pickup_house_no, pickup_street, pickup_flat_no
 * OR the delivery_* mirror set). No payload changes; only the UI
 * collapses cleaner.
 *
 * Props
 *   id            : "pickup" | "delivery" — drives field prefixes &
 *                   test-ids so two instances can coexist.
 *   label         : Header label shown above the input.
 *   placeholder   : Autocomplete placeholder.
 *   value         : Current `<id>_address` string from form state.
 *   onChange      : (string) => void   when user types in the field.
 *   onSelectLocation : (loc) => void   when an autocomplete suggestion
 *                                       is picked (carries lat/lng).
 *   onMapPick     : ()      => void   open the existing MapPicker flow.
 *   structured    : { house_no, street, flat_no } current values.
 *   onFieldChange : (subfield, value) => void  updates structured
 *                                              parts on the parent form.
 *   onCurrentLocation : (loc) => void — same shape as onSelectLocation;
 *                                       wired to the browser geolocation
 *                                       + reverse-geocode helper below.
 */
export default function SmartAddressInput({
  id,
  label,
  placeholder,
  value,
  onChange,
  onSelectLocation,
  onMapPick,
  structured,
  onFieldChange,
  onCurrentLocation,
}) {
  // Iteration 63 — show structured fields by default so customers
  // don't have to discover the "Add flat / house no." disclosure. The
  // toggle is preserved so power users can still collapse them.
  const [expanded, setExpanded] = useState(true);
  const [locating, setLocating] = useState(false);

  // Auto-expand the structured fields if the form already has values
  // for them (e.g. when the user opens a saved draft via Phase 1's
  // "Resume draft" banner).
  useEffect(() => {
    const hasStructured = (
      (structured?.house_no || "").length > 0 ||
      (structured?.street || "").length > 0 ||
      (structured?.flat_no || "").length > 0
    );
    if (hasStructured) setExpanded(true);
  }, [structured?.house_no, structured?.street, structured?.flat_no]);

  // Reveal the disclosure once the user has picked an address.
  // (kept for future gating; structured fields are now shown by default.)

  const handleUseCurrentLocation = async () => {
    if (!navigator.geolocation) {
      toast.error("Your browser doesn't support location.");
      return;
    }
    setLocating(true);
    try {
      const pos = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 12000,
          maximumAge: 60000,
        });
      });
      const { latitude: lat, longitude: lng } = pos.coords;
      // Reverse-geocode via Nominatim — free, India-friendly.
      const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1&accept-language=en`;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(`Reverse geocode failed: ${res.status}`);
      const data = await res.json();
      const addr = data?.display_name || "";
      if (!addr) throw new Error("Could not resolve your location to an address.");
      if (onCurrentLocation) {
        onCurrentLocation({
          address: addr,
          lat,
          lng,
          osm_id: data.osm_id,
          place_id: data.place_id,
        });
      } else if (onSelectLocation) {
        // Fallback to the same handler the autocomplete uses.
        onSelectLocation({
          address: addr,
          lat,
          lng,
          osm_id: data.osm_id,
          place_id: data.place_id,
        });
      }
      toast.success("Used your current location");
      setExpanded(true);
    } catch (err) {
      const code = err?.code;
      let msg = "Could not get your location.";
      if (code === 1) msg = "Location permission denied. Allow access in your browser.";
      else if (code === 2) msg = "Location unavailable. Try again outdoors or check GPS.";
      else if (code === 3) msg = "Location request timed out.";
      else if (err?.message) msg = err.message;
      toast.error(msg);
    } finally {
      setLocating(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={`smart-address-${id}`}>
      <Label className="text-sm font-medium text-slate-700">{label}</Label>
      <div className="flex gap-2">
        <IndiaAddressAutocomplete
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onSelectLocation={(loc) => {
            onSelectLocation(loc);
            setExpanded(true);
          }}
          className="rounded-xl border-slate-200 h-11 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20 flex-1"
          required
          data-testid={`order-${id}-input`}
        />
        <Button
          type="button"
          variant="outline"
          onClick={handleUseCurrentLocation}
          disabled={locating}
          className="rounded-xl h-11 px-3 border-slate-200 hover:border-[#FF6B00] hover:text-[#FF6B00]"
          title="Use my current location"
          data-testid={`order-${id}-current-location-btn`}
        >
          {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onMapPick}
          className="rounded-xl h-11 px-3 border-slate-200 hover:border-[#FF6B00] hover:text-[#FF6B00]"
          title="Pick on map"
          data-testid={`order-${id}-map-btn`}
        >
          <MapPin className="h-4 w-4" />
        </Button>
      </div>

      {/* Structured details (house no, street, flat) — shown by
          default per iteration 63 so customers can fill them in the
          same flow as the main address. Still collapsible. */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="inline-flex items-center gap-1 text-xs font-semibold text-[#FF6B00] hover:text-[#e55f00] mt-1"
        data-testid={`order-${id}-toggle-details-btn`}
        aria-expanded={expanded}
      >
        {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        {expanded ? "Hide flat / house details" : "Add flat / house no."}
      </button>
      {expanded && (
        <div
          className="grid grid-cols-3 gap-2 mt-1"
          data-testid={`${id}-address-details-row`}
        >
          <Input
            placeholder="House No"
            value={structured?.house_no || ""}
            onChange={(e) => onFieldChange("house_no", e.target.value)}
            maxLength={80}
            className="rounded-lg border-slate-200 h-9 text-sm focus:border-[#FF6B00] focus:ring-[#FF6B00]/20"
            data-testid={`order-${id}-house-no`}
          />
          <Input
            placeholder="Street / Landmark"
            value={structured?.street || ""}
            onChange={(e) => onFieldChange("street", e.target.value)}
            maxLength={80}
            className="rounded-lg border-slate-200 h-9 text-sm focus:border-[#FF6B00] focus:ring-[#FF6B00]/20"
            data-testid={`order-${id}-street`}
          />
          <Input
            placeholder="Flat / Apt No"
            value={structured?.flat_no || ""}
            onChange={(e) => onFieldChange("flat_no", e.target.value)}
            maxLength={80}
            className="rounded-lg border-slate-200 h-9 text-sm focus:border-[#FF6B00] focus:ring-[#FF6B00]/20"
            data-testid={`order-${id}-flat-no`}
          />
        </div>
      )}
    </div>
  );
}
